import { Briefcase } from "lucide-react";
import { NavigationItem } from "../../types";
import { BuilderContext } from "../context";

/**
 * Result of attempting to add a sub-section. `present` indicates the section
 * "exists" for the user (controls Work-group visibility), even if its
 * children list is empty after filtering. `item` is the actual nav node to
 * push, or null when nothing should be pushed.
 */
interface SubsectionResult {
  present: boolean;
  item: NavigationItem | null;
}

function buildTicketsItem(ctx: BuilderContext): SubsectionResult {
  const { featureFlags } = ctx;
  if (!featureFlags.canViewTickets) return { present: false, item: null };

  const ticketItems: NavigationItem[] = [];

  if (featureFlags.canViewTickets) {
    ticketItems.push({
      id: "my-tickets",
      name: "My Tickets",
      href: "/tickets/my-tickets",
    });
  }

  if (featureFlags.canViewInternalTickets) {
    ticketItems.push({
      id: "all-tickets",
      name: "All Tickets",
      href: "/tickets/all-tickets",
    });
  }

  if (featureFlags.canCreateTickets) {
    ticketItems.push({
      id: "create-ticket",
      name: "Create Ticket",
      href: "/tickets/create",
    });
  }

  if (featureFlags.canUseTicketTemplates) {
    ticketItems.push({
      id: "ticket-templates",
      name: "Templates",
      href: "/tickets/templates",
    });
  }

  return {
    present: true,
    item: {
      id: "tickets",
      name: "Tickets",
      href: "/tickets",
      children: ticketItems,
    },
  };
}

function buildTasksItem(ctx: BuilderContext): SubsectionResult {
  const { featureFlags } = ctx;
  if (!(featureFlags.canViewTasks || featureFlags.canViewMyTasks)) {
    return { present: false, item: null };
  }

  const taskItems: NavigationItem[] = [];

  if (featureFlags.canViewAllTasks) {
    taskItems.push({ id: "all-tasks", name: "All Tasks", href: "/tasks" });
  }

  if (featureFlags.canViewMyTasks) {
    taskItems.push({ id: "my-tasks", name: "My Tasks", href: "/tasks/my-tasks" });
  }

  if (featureFlags.canCreateTasks) {
    taskItems.push({ id: "create-tasks", name: "Create Task", href: "/tasks/create" });
  }

  // Section presence is determined by canViewTasks||canViewMyTasks, but the
  // tasks node itself is only pushed when it has at least one child.
  if (taskItems.length === 0) return { present: true, item: null };

  return {
    present: true,
    item: {
      id: "tasks",
      name: "Tasks",
      href: "/tasks",
      children: taskItems,
    },
  };
}

function buildTimeTrackingItem(ctx: BuilderContext): SubsectionResult {
  const { isClientUser, featureFlags } = ctx;
  if (isClientUser || !featureFlags.showTimeTrackingMenu) {
    return { present: false, item: null };
  }

  const timeItems: NavigationItem[] = [];

  if (featureFlags.canViewAllTimeEntries) {
    timeItems.push({
      id: "time-overview",
      name: "Overview",
      href: "/time-tracking",
    });
  }

  if (featureFlags.canCreateTimeEntries) {
    timeItems.push({
      id: "log-time",
      name: "Log Time",
      href: "/time-tracking/log",
    });
  }

  if (featureFlags.canViewAllTimeEntries) {
    timeItems.push({
      id: "my-time-entries",
      name: "My Time Entries",
      href: "/time-tracking/my-entries",
    });
    timeItems.push({
      id: "time-entries",
      name: "All Time Entries",
      href: "/time-tracking/entries",
    });
  }

  if (featureFlags.canViewReports) {
    timeItems.push({
      id: "time-reports",
      name: "Reports",
      href: "/time-tracking/reports",
    });
  }

  return {
    present: true,
    item: {
      id: "time-tracking",
      name: "Time Tracking",
      href: "/time-tracking",
      children: timeItems,
    },
  };
}

export function buildWorkSection(ctx: BuilderContext): NavigationItem | null {
  const { isAdmin, isCRM, isEmployee } = ctx;
  const workChildren: NavigationItem[] = [];
  let hasWorkItems = false;

  // Tickets section
  const tickets = buildTicketsItem(ctx);
  if (tickets.present) hasWorkItems = true;
  if (tickets.item) workChildren.push(tickets.item);

  // Tasks section
  const tasks = buildTasksItem(ctx);
  if (tasks.present) hasWorkItems = true;
  if (tasks.item) workChildren.push(tasks.item);

  // Resource Allocation - Admin only (owners/managers)
  if (isAdmin) {
    hasWorkItems = true;
    workChildren.push({
      id: "resource-allocation",
      name: "Resource Allocation",
      href: "/dashboard/allocate",
    });
  }

  // MSP Documentation section - Available to admins, CRM users, and WTC employees
  if (isAdmin || isCRM || isEmployee) {
    hasWorkItems = true;
    workChildren.push({
      id: "msp-docs",
      name: "MSP Docs",
      href: "/msp-docs",
    });
  }

  // Support Inbox - browse any client company's support inbox. Employee-only:
  // the backing API (requireAnyAuthenticatedUserAuthJwtOrSession) restricts to employees.
  if (isAdmin || isEmployee) {
    hasWorkItems = true;
    workChildren.push({
      id: "support-inbox",
      name: "Support Inbox",
      href: "/work/support-inbox",
    });
  }

  // Time Tracking section
  const time = buildTimeTrackingItem(ctx);
  if (time.present) hasWorkItems = true;
  if (time.item) workChildren.push(time.item);

  if (!hasWorkItems) return null;

  return {
    id: "work",
    name: "Work",
    href: "/work", // Landing page for all work items
    icon: Briefcase,
    visible: true,
    order: 4,
    children: workChildren,
  };
}
